import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { getToken, setToken, removeToken } from "@/utils/auth";
import { getUserInfo, getUserMoreInfo, logout as logoutApi } from "@/api/user";
import {
  getTutorHomeTask,
  getGradeHonorDetail,
  getPetInfo,
  getMilestoneList,
  growthSystemIsFirstLogin,
} from "@/api/dashboard";
import { getTutorTrail } from "@/api/tutordata";
import { getSelfScheduleLimitCheck } from "@/api/releaseTime";
import { GetTeacherToken } from "@/api/classtable";
import dfAvatar from "@/assets/img/common/acadsoc.jpg";

function stopSignalR() {
  // 动态导入，避免与 utils/signalR → userStore 循环依赖
  import("@/utils/signalR")
    .then((m) => m.default?.stop?.())
    .catch(() => {});
}

/** 通知 TutorClient 壳当前老师身份（Vue2 window.GetTeachUserData） */
function pushTeachUserDataToClient(tuidVal, tokenVal) {
  try {
    if (typeof window.GetTeachUserData !== "function") {
      console.log("window.GetTeachUserData客户端方法不存在");
      return;
    }
    if (!tuidVal || tuidVal <= 0) return;
    console.log("调用window.GetTeachUserData上传参数给客户端");
    window.GetTeachUserData(
      JSON.stringify({
        TUID: tuidVal,
        Token: tokenVal || "",
      })
    );
  } catch (e) {
    console.warn("GetTeachUserData failed", e);
  }
}

export const useUserStore = defineStore('user', () => {
  const token = ref(getToken() || '')
  const expiresTime = ref(0)
  const roles = ref([])
  const tuid = ref(0)
  const fullName = ref('')
  const avatar = ref('')
  const defaultAvatar = dfAvatar
  const email = ref('')
  /** Login / tutor account used by Tencent Classroom & CustomAIStory (Vue2 user.account) */
  const account = ref('')
  const authStr = ref('')
  const actoken = ref('')
  const isFirstLogin = ref(false)
  const isFranchiseeUser = ref(false)
  const flowers = ref(0)
  const teachingPoints = ref(0)
  const integralLevel = ref('')
  const growthSystemScore = ref(0)
  const growthSystemLevel = ref(0)
  const growthSystemUnclaimedPoints = ref(0)
  const growthSystemRemainingPoints = ref(0)
  const growthSystemTaskList = ref([])
  const growthSystemJoinDay = ref(0)
  const growthSystemLevelMedal = ref([])
  const growthSystemMilestoneList = ref([])
  const petInfo = ref({
    AlterNum: 0,
    IsAdopt: null,
    IsCanModified: null,
    PetLevel: 0,
    PetName: "",
  })
  const isFirstGrowthSystemLogin = ref(false)
  const transformPeriodCount = ref(0)
  const isGrowthSystemTestGroup = ref(false)
  const showDashboardRank = ref(false)
  const isTuEyeProtectionMode = ref(0)
  const isNeedToImprovePersonalInformation = ref(false)
  /** Vue2 user.isNeedUpdatePhone — 强制绑手机弹层 */
  const isNeedUpdatePhone = ref(false)
  const awaitApproval = ref(false)
  const isInternationalOrCustom = ref(0)
  const retrieveEmail = ref({ email: "", step: 1 });
  const openSelfSchedule = ref(false);
  const openSelfCancel = ref(false);
  const disableComment = ref("");
  const limitCheck = ref(null);
  const feedBackMsg = ref(false);
  const auditFailure = ref({ dialog: false, text: "" });

  const userName = computed(() => {
    if (tuid.value && fullName.value) return `${fullName.value} (${tuid.value})`
    return fullName.value || ''
  })

  /** Vue2 getter growthSystemTaskListLength */
  const growthSystemTaskListLength = computed(() => {
    const list = growthSystemTaskList.value
    if (!list) return 0
    let len = 0
    list.forEach((v) => {
      len += v.TaskDataDetail?.length || 0
    })
    return len
  })

  /** Vue2 getter growthSystemTaskListCompletedLen */
  const growthSystemTaskListCompletedLen = computed(() => {
    const list = growthSystemTaskList.value
    if (!list) return 0
    let len = 0
    list.forEach((v) => {
      v.TaskDataDetail?.forEach((m) => {
        if (m.Status == 2 && m.ReceiveStatus == 1) len += 1
      })
    })
    return len
  })

  function loginThree(authInfo) {
    const accessToken = authInfo?.access_token
    // Hash may carry access_token=null — do not persist as a real token
    if (!accessToken || String(accessToken).toLowerCase() === 'null' || String(accessToken).toLowerCase() === 'undefined') {
      token.value = ''
      removeToken()
      return ''
    }
    token.value = accessToken
    if (authInfo.expires_in) {
      const curr = new Date()
      expiresTime.value = curr.setTime(curr.getTime() + 1000 * authInfo.expires_in)
    }
    setToken(accessToken)
    return accessToken
  }

  function loginAuto(authInfo) {
    roles.value = []
    token.value = authInfo.access_token
    if (authInfo.expires_in) {
      const curr = new Date()
      expiresTime.value = curr.setTime(curr.getTime() + 1000 * authInfo.expires_in)
    }
    setToken(authInfo.access_token)
    if (!getToken()) throw new Error('Token is Empty, Login Fail.')
  }

  function setAuthStr(str) {
    authStr.value = str
  }

  function setTuEyeProtectionMode(mode) {
    isTuEyeProtectionMode.value = mode
  }

  function setInternationalOrCustom(v) {
    isInternationalOrCustom.value = v
  }

  async function fetchUserInfo() {
    const data = await getUserInfo();
    tuid.value = data.tuid;
    roles.value = ["admin"];
    fullName.value = data.fullName;
    if (data.openSelfSchedule != null) openSelfSchedule.value = !!data.openSelfSchedule;
    if (data.openSelfCancel != null) openSelfCancel.value = !!data.openSelfCancel;
    if (data.disableComment != null) disableComment.value = data.disableComment;
    // Vue2 GetUserInfo 后 setAcToken：拉课堂 Token 并回传客户端
    setAcToken().catch(() => {});
    return { ...data, roles: roles.value };
  }

  /** Vue2 user.setAcToken */
  async function setAcToken() {
    if (actoken.value) {
      pushTeachUserDataToClient(tuid.value, actoken.value);
      return actoken.value;
    }
    try {
      const body = await GetTeacherToken();
      const tokenVal = body?.token || body?.Token || "";
      if (tokenVal) actoken.value = tokenVal;
      pushTeachUserDataToClient(tuid.value, actoken.value);
      return actoken.value;
    } catch (e) {
      console.warn("GetTeacherToken failed", e);
      pushTeachUserDataToClient(tuid.value, actoken.value);
      return actoken.value;
    }
  }

  function notifyTutorClientUserData() {
    pushTeachUserDataToClient(tuid.value, actoken.value);
  }

  async function fetchUserMoreInfo() {
    try {
      const data = await getUserMoreInfo()
      isFirstLogin.value = !!data.isfirstLogin
      if (data.avatar) avatar.value = data.avatar
      if (data.email) email.value = data.email
      if (data.account != null) account.value = data.account || ''
      if (data.isFranchiseeUser != null) isFranchiseeUser.value = !!data.isFranchiseeUser
      if (data.flowers != null) flowers.value = data.flowers
      if (data.teachingPoints != null) teachingPoints.value = data.teachingPoints
      if (data.integralLevel != null) integralLevel.value = data.integralLevel
      if (data.growthSystemScore != null) growthSystemScore.value = data.growthSystemScore
      if (data.transformPeriodCount != null) transformPeriodCount.value = data.transformPeriodCount
      // API: IsTestGroup (Vue2 SET_GROWTHSYSTEM); also accept camelCase variants
      if (data.IsTestGroup != null || data.isTestGroup != null) {
        isGrowthSystemTestGroup.value = !!(data.IsTestGroup ?? data.isTestGroup)
      } else if (data.isGrowthSystemTestGroup != null) {
        isGrowthSystemTestGroup.value = !!data.isGrowthSystemTestGroup
      }
      if (data.isTuEyeProtectionMode != null) {
        isTuEyeProtectionMode.value = data.isTuEyeProtectionMode
      }
      if (data.isNeedToImprovePersonalInformation != null) {
        isNeedToImprovePersonalInformation.value = !!data.isNeedToImprovePersonalInformation
      }
      if (data.isNeedUpdatePhone != null) {
        isNeedUpdatePhone.value = !!data.isNeedUpdatePhone
      }
      if (data.awaitApproval != null) awaitApproval.value = !!data.awaitApproval;
      if (data.openSelfSchedule != null) openSelfSchedule.value = !!data.openSelfSchedule;
      if (data.openSelfCancel != null) openSelfCancel.value = !!data.openSelfCancel;
      if (data.disableComment != null) disableComment.value = data.disableComment;
      return data;
    } catch (e) {
      console.warn('GetUserMoreInfo failed', e)
    }
  }

  async function logOut() {
    try {
      await logoutApi()
    } catch (_) {
      /* ignore */
    }
    stopSignalR()
    roles.value = []
    token.value = ''
    removeToken()
  }

  function fedLogOut() {
    stopSignalR()
    roles.value = [];
    token.value = "";
    removeToken();
  }

  function setGrowthSystemLevel(level) {
    growthSystemLevel.value = Number(level) || 0
  }

  /** Vue2: commit('SET_GROWTHSYSTEM', data.IsTestGroup) from GetTutorHomeTask */
  function setGrowthSystemTestGroup(flag) {
    isGrowthSystemTestGroup.value = !!flag
  }

  /** Vue2 store action GetTutorHomeTask — sets growth fields from Body */
  async function fetchTutorHomeTask() {
    try {
      const data = await getTutorHomeTask()
      if (!data) return null
      if (data.IsTestGroup != null || data.isTestGroup != null) {
        isGrowthSystemTestGroup.value = !!(data.IsTestGroup ?? data.isTestGroup)
      }
      if (data.Score != null) growthSystemScore.value = data.Score
      if (data.Level != null) growthSystemLevel.value = Number(data.Level) || 0
      if (data.UnclaimedPoints != null) {
        growthSystemUnclaimedPoints.value = data.UnclaimedPoints
      }
      if (data.RemainingPoints != null) {
        growthSystemRemainingPoints.value = data.RemainingPoints
      }
      if (data.TaskList != null) growthSystemTaskList.value = data.TaskList
      if (data.JoinDay != null) growthSystemJoinDay.value = data.JoinDay
      return data
    } catch (e) {
      console.warn('GetTutorHomeTask failed', e)
      return null
    }
  }

  /** Vue2 GetGradeHonorDetail */
  async function fetchGradeHonorDetail() {
    try {
      const data = await getGradeHonorDetail()
      if (data != null) growthSystemLevelMedal.value = data
      return data
    } catch (e) {
      console.warn('GetGradeHonorDetail failed', e)
      return null
    }
  }

  /** Vue2 GetPetInfo */
  async function fetchPetInfo() {
    try {
      const data = await getPetInfo()
      if (data) petInfo.value = data
      return data
    } catch (e) {
      console.warn('GetPetInfo failed', e)
      return null
    }
  }

  /** Vue2 GetMilestoneList */
  async function fetchMilestoneList() {
    try {
      const data = await getMilestoneList()
      if (data) growthSystemMilestoneList.value = data
      return data
    } catch (e) {
      console.warn('GetMilestoneList failed', e)
      return null
    }
  }

  /** Vue2 GrowthSystemIsFirstLogin */
  async function fetchGrowthSystemIsFirstLogin() {
    try {
      const data = await growthSystemIsFirstLogin()
      isFirstGrowthSystemLogin.value = !!data
      return data
    } catch (e) {
      console.warn('GrowthSystemIsFirstLogin failed', e)
      return null
    }
  }

  /** Vue2 GetTutorTrail — Enrolment Tol count */
  async function fetchTutorTrail() {
    try {
      const res = await getTutorTrail(tuid.value)
      if (res?.data?.ErrorCode === 0 && res.data.Body != null) {
        transformPeriodCount.value = Number(res.data.Body) || 0
      }
      return transformPeriodCount.value
    } catch (e) {
      console.warn('GetTutorTrail failed', e)
      return transformPeriodCount.value
    }
  }

  function setDashboardRank(flag) {
    showDashboardRank.value = !!flag
  }

  function setFeedBackMsg(flag) {
    feedBackMsg.value = !!flag
  }

  function setIntegralLevel(level) {
    integralLevel.value = level || ''
  }

  function updateEmail(val) {
    email.value = val || ''
  }

  function setFailure(failure) {
    auditFailure.value = {
      dialog: !!failure?.dialog,
      text: failure?.text || "",
    };
  }

  async function getLimit() {
    if (limitCheck.value && limitCheck.value.SelfSchedulesEnable) {
      return limitCheck.value;
    }
    const data = await getSelfScheduleLimitCheck({});
    limitCheck.value = data || null;
    return limitCheck.value;
  }

  return {
    token,
    expiresTime,
    roles,
    tuid,
    fullName,
    avatar,
    defaultAvatar,
    email,
    account,
    authStr,
    actoken,
    isFirstLogin,
    isFranchiseeUser,
    flowers,
    teachingPoints,
    integralLevel,
    growthSystemScore,
    growthSystemLevel,
    growthSystemUnclaimedPoints,
    growthSystemRemainingPoints,
    growthSystemTaskList,
    growthSystemJoinDay,
    growthSystemLevelMedal,
    growthSystemMilestoneList,
    petInfo,
    isFirstGrowthSystemLogin,
    transformPeriodCount,
    isGrowthSystemTestGroup,
    showDashboardRank,
    isTuEyeProtectionMode,
    isNeedToImprovePersonalInformation,
    isNeedUpdatePhone,
    awaitApproval,
    isInternationalOrCustom,
    retrieveEmail,
    openSelfSchedule,
    openSelfCancel,
    disableComment,
    limitCheck,
    feedBackMsg,
    auditFailure,
    userName,
    growthSystemTaskListLength,
    growthSystemTaskListCompletedLen,
    loginThree,
    loginAuto,
    setAuthStr,
    setTuEyeProtectionMode,
    setInternationalOrCustom,
    setGrowthSystemLevel,
    setGrowthSystemTestGroup,
    setDashboardRank,
    setFeedBackMsg,
    setIntegralLevel,
    updateEmail,
    setFailure,
    fetchUserInfo,
    setAcToken,
    notifyTutorClientUserData,
    fetchUserMoreInfo,
    fetchTutorHomeTask,
    fetchGradeHonorDetail,
    fetchPetInfo,
    fetchMilestoneList,
    fetchGrowthSystemIsFirstLogin,
    fetchTutorTrail,
    logOut,
    fedLogOut,
    getLimit,
  };
});
